import React from 'react';
import { connect } from 'react-redux';
import { Table, Spinner } from 'react-bootstrap';
import { PageStatus, Assets } from 'enums';
import { SurveysAPI } from '../../API';
import { formatDate } from '../../Utils/formatDate';
import { branding } from '../../config/portalBranding';
import { surveyCallbackDict } from '../../Languages/SurveyCallbackTranslations';
import LanguageToggle from '../Common/LanguageToggle';
import { applyOutcomeLanguage } from './applyOutcomeLanguage';

class SurveyOverQuotaCallbackPage extends React.Component<any, any> {
    constructor(props) {
        super(props);
        this.state = {
            survey: null,
            status: PageStatus.None,
            error: null,
        };
    }

    componentDidMount() {
        const fullOqcpUrl = window.location.href;
        const queryStr = fullOqcpUrl.split('?')[1] || '';
        const urlSearchingParams = new URLSearchParams(queryStr);
        const surveyId = urlSearchingParams.get('surveyid');
        const userId = urlSearchingParams.get('userid');
        if (!!surveyId && !!userId) {
            // No outcome call from this page: the survey platform's callback
            // URL is on the ADMIN domain, admin records the hit and relays it
            // to this country backend (which decides the final status), then
            // forwards the panelist here. This page only renders.
            this.fetchOqcpSurvey(surveyId, userId);
        }
    }

    // Read-only fetch — the hit was already recorded by the DX (admin) layer
    // upstream. No status updates, no redirects on success; this page is terminal.
    fetchOqcpSurvey(surveyId, userId) {
        Promise.resolve()
            .then(() => this.setState({ status: PageStatus.Loading }))
            .then(() => SurveysAPI.getSurveyDisplayDataPlain({ surveyId, userId }))
            .then((survey) => {
                // Render in the panelist's account language (users.language).
                this.props.applyOutcomeLanguage(survey?.language);
                this.setState({ survey, status: PageStatus.Loaded });
            })
            .catch((error) => {
                this.setState({ status: PageStatus.Error, error: error.message });
            });
    }

    render() {
        const { survey, status, error } = this.state;
        const dict = surveyCallbackDict[this.props.language || 'en'] || surveyCallbackDict.en;
        const loading = status === PageStatus.Loading;
        const errored = status === PageStatus.Error;
        const detail = survey?.surveysDetails;
        const logoSrc = branding.logo || Assets.Logo2;
        const fullName = [survey?.user?.firstName, survey?.user?.lastName].filter(Boolean).join(' ');
        const pointsRaw = detail?.survey?.overquota;
        const pointsDisplay = (pointsRaw === undefined || pointsRaw === null)
            ? ''
            : `${pointsRaw}${detail?.survey?.pointAllocationType !== 'Auto' ? dict.pendingSuffix : ''}`;
        const nowStr = formatDate(new Date());

        return (
            <div className="container-fluid" style={{ padding: '40px 16px', minHeight: '100vh', position: 'relative' }}>
                {/* Top-right language toggle (matches /privacy-policy placement).
                    Renders nothing on en-only portals. */}
                <div style={{ position: 'absolute', top: 12, right: 16, zIndex: 1200 }}>
                    <LanguageToggle />
                </div>
                <div style={{ maxWidth: '960px', margin: '0 auto' }}>
                    <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                        <img
                            src={logoSrc}
                            alt={branding.name}
                            style={{ maxHeight: '90px', maxWidth: '320px', objectFit: 'contain' }}
                        />
                    </div>

                    <h1 style={{ textAlign: 'center', margin: '0 0 28px', fontWeight: 700 }}>
                        {dict['heading.overquota']}
                    </h1>

                    {loading && (
                        <div style={{ textAlign: 'center', padding: '24px 0' }}>
                            <Spinner animation="border" />
                        </div>
                    )}

                    {errored && (
                        <div className="alert alert-warning" role="alert" style={{ textAlign: 'center', fontSize: '0.9rem' }}>
                            {dict.errorLoad} ({error || dict.unknownError})
                        </div>
                    )}

                    {!loading && (
                        <>
                            <Table bordered hover responsive style={{ background: '#fff', marginBottom: '24px' }}>
                                <thead>
                                    <tr>
                                        <th style={{ textAlign: 'center' }}>{dict['table.sNo']}</th>
                                        <th>{dict['table.userName']}</th>
                                        <th>{dict['table.surveyName']}</th>
                                        <th style={{ textAlign: 'center' }}>{dict['table.points']}</th>
                                        <th>{dict['table.dateTime']}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td style={{ textAlign: 'center' }}>1</td>
                                        <td>{fullName || '-'}</td>
                                        <td>{detail?.survey?.name || '-'}</td>
                                        <td style={{ textAlign: 'center' }}>{pointsDisplay || '-'}</td>
                                        <td>{nowStr}</td>
                                    </tr>
                                </tbody>
                            </Table>

                            {detail?.survey?.disclaimer && (
                                <div style={{ background: '#fff', padding: '16px 20px', border: '1px solid #dee2e6', borderRadius: '4px' }}>
                                    <strong>{dict.disclaimer}</strong>
                                    <div dangerouslySetInnerHTML={{ __html: detail.survey.disclaimer }} />
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        );
    }
}

const mapStateToProps = (state: any) => ({
    language: state.adminUser.adminUser.language,
});

const mapDispatchToProps = (dispatch: any) => ({
    applyOutcomeLanguage: (lang: any) => applyOutcomeLanguage(dispatch, lang),
});

export default connect(mapStateToProps, mapDispatchToProps)(SurveyOverQuotaCallbackPage);
